import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { ShieldCheck, Clock, MapPin } from "lucide-react";

export default function AdminCoaches() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => api.get("/admin/coaches", { params: filter ? { status: filter } : {} })
    .then((r) => setRows(r.data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, [filter]);

  const decide = async (row, decision) => {
    try {
      await api.post(`/admin/coaches/${row.id}/review`, { decision, verified: decision === "approved" });
      toast.success(decision === "approved" ? `${row.name} is live and verified` : `${row.name} rejected`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-coaches-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Admin</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Coach approvals</h1>
      <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">New coaches stay hidden from the marketplace until you approve them. Approving also gives them the verified badge.</p>

      <div className="flex flex-wrap gap-2 mt-8">
        {[{ v: "pending", l: "Pending" }, { v: "approved", l: "Approved" }, { v: "rejected", l: "Rejected" }, { v: "", l: "All" }].map((t) => (
          <button key={t.v || "all"} data-testid={`coach-filter-${t.v || "all"}`} onClick={() => setFilter(t.v)}
            className={`text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors duration-200 ${
              filter === t.v ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 text-zinc-400 hover:border-white/40"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-white/10">
        {rows.length === 0 && <p data-testid="no-coach-applications" className="p-10 text-sm text-zinc-500">Nothing here right now.</p>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`coach-app-${r.id}`} className="p-5 bg-[#121212] border-b border-white/10 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <p className="display uppercase font-bold text-lg leading-none flex items-center gap-2">
                {r.name}
                {r.verified && <ShieldCheck data-testid={`verified-icon-${r.id}`} className="w-4 h-4 text-[#00e5ff]" />}
              </p>
              <p className="text-[11px] text-zinc-500 mt-1">{r.email}</p>
            </div>
            <div className="md:col-span-3 text-xs text-zinc-400">
              <p>{r.category}</p>
              <p className="flex items-center gap-1 mt-1 text-zinc-500"><MapPin className="w-3 h-3" /> {r.city || "no city set"}{r.region ? `, ${r.region}` : ""}</p>
            </div>
            <div className="md:col-span-3 text-xs text-zinc-400">
              <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.experience_years} yrs</p>
              <p className="text-zinc-500 mt-1">{r.certifications?.length ? r.certifications.join(", ") : "no certifications listed"}</p>
            </div>
            <div className="md:col-span-2 md:text-right space-y-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 block">{r.approval_status}</span>
              {r.approval_status !== "approved" && (
                <button data-testid={`approve-btn-${r.id}`} onClick={() => decide(r, "approved")}
                  className="text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
                  Approve & verify
                </button>
              )}
              {r.approval_status !== "rejected" && (
                <button data-testid={`reject-btn-${r.id}`} onClick={() => decide(r, "rejected")}
                  className="block md:ml-auto text-[10px] uppercase tracking-widest border border-white/15 text-zinc-400 px-3 py-2 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors duration-200">
                  Reject
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
